// Animates a number toward `target` on every change (counters + estimator).
import { useEffect, useRef, useState } from 'react';

const REDUCE = typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * <AnimatedNumber target={150} format={(n) => n.toLocaleString('fr-FR')} />
 * @param target      target value (number)
 * @param decimals    number of decimals (def. 0)
 * @param duration    ms (def. 700)
 * @param format      (n) => string  display formatting
 * @param startOnView if true, starts only when visible (counters)
 */
export default function AnimatedNumber({ target, decimals = 0, duration = 700, format, startOnView = false }) {
  const [val, setVal] = useState(startOnView ? 0 : target);
  const fromRef = useRef(startOnView ? 0 : target);
  const rafRef = useRef(null);
  const boxRef = useRef(null);
  const [armed, setArmed] = useState(!startOnView);

  // Starts when the element enters the viewport (counter mode)
  useEffect(() => {
    if (!startOnView || armed) return;
    if (REDUCE) { setArmed(true); return; }
    const check = () => {
      const el = boxRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.top < vh * 0.85) { setArmed(true); cleanup(); }
    };
    const cleanup = () => {
      window.removeEventListener('scroll', check, true);
      window.removeEventListener('resize', check);
    };
    window.addEventListener('scroll', check, true);
    window.addEventListener('resize', check);
    check();
    return cleanup;
  }, [startOnView, armed]);

  useEffect(() => {
    if (!armed) return;
    if (REDUCE) { setVal(target); return; }
    const from = fromRef.current;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (target - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, armed, duration]);

  const rounded = decimals ? Number(val.toFixed(decimals)) : Math.round(val);
  const text = format ? format(rounded) : String(rounded);
  return <span ref={boxRef}>{text}</span>;
}
